/**
 * Component: ThemeToggle
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R14
 *
 * Theme toggle button component for switching between light and dark mode.
 * FRONTEND-WEB-V1-R10: Toggle between light and dark mode.
 * FRONTEND-WEB-V1-R14: WCAG 2.1 AA compliant with keyboard navigation.
 *
 * Risk Links: R10
 * FMEA Links: FM10
 */

import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme, type Theme } from '@/contexts';

/**
 * ThemeToggle component props.
 */
interface ThemeToggleProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Theme toggle component with dropdown menu.
 *
 * FRONTEND-WEB-V1-R10: Provides theme selection (light, dark, system).
 * FRONTEND-WEB-V1-R14: Accessible dropdown with keyboard navigation.
 *
 * Usage:
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle({ className }: ThemeToggleProps): React.ReactElement {
  const { t } = useTranslation();
  const { theme, resolvedTheme, setTheme } = useTheme();

  /**
   * Get the icon for the current resolved theme.
   */
  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;

  /**
   * Handle theme selection.
   */
  const handleThemeChange = (newTheme: Theme): void => {
    setTheme(newTheme);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          aria-label={t('settings.theme', 'Toggle theme')}
        >
          <ThemeIcon className="h-5 w-5" />
          <span className="sr-only">{t('settings.theme', 'Toggle theme')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleThemeChange('light')}
          className={theme === 'light' ? 'bg-accent' : ''}
        >
          <Sun className="mr-2 h-4 w-4" />
          {t('settings.themes.light', 'Light')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleThemeChange('dark')}
          className={theme === 'dark' ? 'bg-accent' : ''}
        >
          <Moon className="mr-2 h-4 w-4" />
          {t('settings.themes.dark', 'Dark')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleThemeChange('system')}
          className={theme === 'system' ? 'bg-accent' : ''}
        >
          <Monitor className="mr-2 h-4 w-4" />
          {t('settings.themes.system', 'System')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Simple theme toggle button (just toggles light/dark).
 *
 * FRONTEND-WEB-V1-R10: Simple toggle between light and dark.
 *
 * Usage:
 * ```tsx
 * <ThemeToggleSimple />
 * ```
 */
export function ThemeToggleSimple({ className }: ThemeToggleProps): React.ReactElement {
  const { t } = useTranslation();
  const { resolvedTheme, toggleTheme } = useTheme();

  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;
  const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      className={className}
      aria-label={t(`settings.themes.${nextTheme}`, `Switch to ${nextTheme} mode`)}
    >
      <ThemeIcon className="h-5 w-5" />
      <span className="sr-only">
        {t(`settings.themes.${nextTheme}`, `Switch to ${nextTheme} mode`)}
      </span>
    </Button>
  );
}

export default ThemeToggle;
